import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

import { getNotificationsService } from "../../services/userServices";

import { setIsStopTimer } from "../../redux/actions/timerActions/timerActions";
import CustomerOrderButton from "./CustomerOrderButton";
import AdminOrderButton from "./AdminOrderButton";

import { decodeJWT } from "../../utils";
import notificationSound from "../../assets/notificationSound/notificationSound.mp3";

const NotificationComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const shownNotifications = useRef(new Map());
  const toastIds = useRef(new Map());

  const token = localStorage.getItem("token");
  const decodedToken = decodeJWT(token);

  const [notifications, setNotifications] = useState([]);

  //* Handle Blocked Pages
  const blockedPages = ["/", "/Sign-Up", "/Forget-Password"];
  const isBlockedPages = blockedPages.includes(location.pathname);

  const [hesabiApiStatus, setHesabiApiStatus] = useState(null);

  //* Handle Get Notifications
  const handleGetNotification = async () => {
    try {
      const { status, data } = await getNotificationsService();
      if (status === 200) {
        setNotifications(data);
      }
    } catch ({ response }) {
      if (response?.status) {
        toast.error(response.data.message);
      }
    }
  };

  //* Handle Fetch And Check Block Pages
  const handleInitial = async () => {
    if (!isBlockedPages) {
      await handleGetNotification();
    }
  };

  //* useEffect for fetching data every 2 seconds
  useEffect(() => {
    const fetchData = async () => {
      await handleInitial();
    };
    fetchData();

    const interval = setInterval(handleInitial, 2000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  //* Handle Notification Sound
  const handleSound = () => {
    const audio = new Audio(notificationSound);
    audio.play().catch((error) => {
      toast.error("پخش صدا با خطا مواجه شد", error);
    });
  };

  const updateNotifications = async () => {
    await handleGetNotification();
  };

  //* Notifications Part
  useEffect(() => {
    if (isBlockedPages) return;
    if (!shownNotifications.current) shownNotifications.current = new Map();

    notifications.forEach((notification) => {
      const { id, transaction_id, tType, customeruser } = notification;
      const key = `${id}-${tType}`;

      //! Prevent Notifications From Appearing Multiple Times
      if (shownNotifications.current.has(key)) return;

      //! Checking Whether The User Is Allowed To See The Notification Or Not
      const isAdmin = decodedToken?.usertype === "ADMIN";
      const isCustomer = decodedToken?.user_id === customeruser;

      if (!isAdmin && !isCustomer) return;

      let toastContent = null;
      let duration;
      let toastId = `toast-${id}`;

      //! Ttype Status "E"
      if (tType === "E") {
        handleSound();
        const adminToastContent = (
          <AdminOrderButton
            onConfirm={updateNotifications}
            onCancel={updateNotifications}
            setHesabiApiStatus={setHesabiApiStatus}
          />
        );
        const customerToastContent = (
          <CustomerOrderButton
            transaction_id={transaction_id}
            toastId={toastId}
            onConfirm={updateNotifications}
            onCancel={updateNotifications}
          />
        );

        toastContent = isAdmin ? adminToastContent : customerToastContent;
        duration = 50000;

        //! Save Notification ID To Close After Status Change
        const id = toast(toastContent, { id: toastId, duration });
        toastIds.current.set(transaction_id, id);
      }

      //! Close Notification After Status Change For Admin
      else if (toastIds.current.has(transaction_id)) {
        setTimeout(() => {
          toast.dismiss(toastIds.current.get(transaction_id));
          toastIds.current.delete(transaction_id);
        }, 1000);
      }

      //! Ttype Status "T" & "L" For Customer
      if (tType === "T" && isCustomer) {
        dispatch(setIsStopTimer());
        handleSound();
        toast.success(`سفارش شما تأیید شد - شماره سفارش: ${transaction_id}`, {
          id: `toast-${key}`,
          duration: 20000,
        });
      } else if (tType === "L" && isCustomer) {
        dispatch(setIsStopTimer());
        handleSound();
        toast.error(`سفارش شما لغو شد - شماره سفارش: ${transaction_id}`, {
          id: `toast-${key}`,
          duration: 20000,
        });
      }

      //! Ttype Status "T" For Admin
      if (tType === "T" && isAdmin && !hesabiApiStatus) {
        dispatch(setIsStopTimer());
        toast.error(
          `تراکنش ثبت گردید اما ارسال به حسابداری ناموفق بود - شماره سفارش: ${transaction_id}`,
          {
            id: `toast-${key}`,
            duration: 20000,
          }
        );
      }

      //! Save Displayed Notification
      shownNotifications.current.set(key, true);
    });
  }, [notifications, decodedToken, isBlockedPages]);

  return null;
};

export default NotificationComponent;
